import {
  downResourceMaterial,
  getResourceMaterialDetail,
  MaterialStatus,
  MaterialType,
  upResourceMaterial
} from '@/api/resourceMaterial';
import LinkSelectForm from '@/components/LinkSelectForm';
import { FORMAT_TIME } from '@/constants/date';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Image,
  message,
  Space,
  Tag
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MaterialForm from '../components/CreateForm';

const ResourceMaterialDetailPage = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const code = searchParams.get('code');
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [isEditing, setIsEditing] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['resource-material-detail', id, code],
    queryFn: async () => {
      if (!id || !code) return null;
      const result = await getResourceMaterialDetail(code, Number(id));
      return result;
    },
  });

  const handleUp = async () => {
    if (!id || !code) return;
    try {
      await upResourceMaterial(code, Number(id));
      messageApi.success('上架成功');
      refetch();
    } catch (error: unknown) {
      messageApi.error('上架失败');
    }
  };

  const handleDown = async () => {
    if (!id || !code) return;
    try {
      await downResourceMaterial(code, Number(id));
      messageApi.success('下架成功');
      refetch();
    } catch (error: unknown) {
      messageApi.error('下架失败');
    }
  };

  const handleEdit = () => {
    if (!material) return;
    setIsEditing(true);
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
    refetch();
  };

  const material = data?.data;

  return (
    <div className="resource-material-detail-page">
      {contextHolder}
      <Card
        title="素材详情"
        loading={isLoading}
        extra={
          <Space>
            <Button onClick={() => navigate(-1)}>返回</Button>
            {!isEditing && (
              <>
                <Button type="primary" onClick={handleEdit}>
                  编辑
                </Button>
                {material?.status === MaterialStatus.Online ? (
                  <Button type="primary" danger onClick={handleDown}>
                    下架
                  </Button>
                ) : (
                  <Button type="primary" onClick={handleUp}>
                    上架
                  </Button>
                )}
              </>
            )}
          </Space>
        }
      >
        {material && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '24px',
            padding: window.innerWidth <= 768 ? '12px' : '24px' 
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontWeight: 500 }}>素材描述</div>
              <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px', minHeight: '80px' }}>
                {material.materialDesc || '-'}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontWeight: 500 }}>素材类型</div>
              <div>
                <Tag color={material.materialType === MaterialType.Single ? 'blue' : 'purple'}>
                  {material.materialType === MaterialType.Single ? '单图' : '双图'}
                </Tag>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '24px',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row' 
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, marginBottom: '8px' }}>排序值</div>
                <div>{material.sort}</div>
              </div>
              <div style={{ flex: 2 }}>
                <div style={{ fontWeight: 500, marginBottom: '8px' }}>展示时间</div>
                <div style={{ 
                  wordBreak: 'break-word',
                  whiteSpace: window.innerWidth <= 768 ? 'normal' : 'nowrap'
                }}>
                  {dayjs(material.startTime).format(FORMAT_TIME)} ~ {dayjs(material.endTime).format(FORMAT_TIME)}
                </div>
              </div>
            </div>

            <div style={{ 
              display: 'grid', 
              gap: '24px', 
              gridTemplateColumns: window.innerWidth <= 768 
                ? '1fr' 
                : material.materialType === MaterialType.Double ? 'repeat(2, 1fr)' : '1fr' 
            }}>
              {material.positionMaterialImageResourceDTOList.map((image, index) => (
                <div
                  key={index}
                  style={{
                    padding: window.innerWidth <= 768 ? '12px' : '24px',
                    background: '#fafafa',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ fontWeight: 500 }}>
                        素材图{material.materialType === MaterialType.Double ? index + 1 : ''}
                      </span>
                      <span style={{ 
                        marginLeft: '8px', 
                        fontSize: '12px', 
                        color: '#999',
                        display: window.innerWidth <= 768 ? 'block' : 'inline' 
                      }}>
                        {material.materialType === MaterialType.Double ? '建议尺寸：528x264' : '建议尺寸：1083x264'}
                      </span>
                    </div>
                    <Image
                      src={image.imageUrl}
                      alt={`素材图片${index + 1}`}
                      style={{
                        width: '100%',
                        height: window.innerWidth <= 768 ? '180px' : '264px',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ fontWeight: 500, marginBottom: '8px' }}>跳转链接</div>
                    <LinkSelectForm
                      disabled
                      value={{
                        notifyType: image.openType,
                        link: image.openUrl,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
              <div style={{ fontWeight: 500 }}>其他信息</div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)', 
                gap: '16px' 
              }}>
                <div>创建人：{material.createUserName}</div>
                <div>创建时间：{dayjs(material.createTime).format(FORMAT_TIME)}</div>
                <div>更新人：{material.updateUserName}</div>
                <div>更新时间：{dayjs(material.updateTime).format(FORMAT_TIME)}</div>
              </div>
            </div>
          </div>
        )}

        <MaterialForm
          open={isEditing}
          onCancel={() => setIsEditing(false)}
          onSuccess={handleEditSuccess}
          mode="edit"
          resourceCode={code!}
          id={Number(id)}
          initialValues={{
            ...material,
            timeRange: [dayjs(material?.startTime), dayjs(material?.endTime)],
          }}
        />
      </Card>
    </div>
  );
};

export default ResourceMaterialDetailPage;
